import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db as firestore, storage } from '../firebase/firebaseConfig';
import { getAllQueuedOps, clearQueuedOp, getPendingBlob, deletePendingBlob } from './db';

/**
 * Flush all pending sync queue ops to Firebase.
 * Always call this BEFORE attaching onSnapshot listeners.
 */
export async function flushQueue(userId) {
  const ops = await getAllQueuedOps();
  if (ops.length === 0) return { flushed: 0, errors: [] };

  // Sort by createdAt ascending so ops apply in order
  ops.sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  let flushed = 0;
  const errors = [];

  for (const op of ops) {
    try {
      if (op.op === 'set') {
        const { photo } = op.payload;
        let thumbnailUrl = photo.thumbnailUrl || '';

        // If there's no remote URL yet, the blob was stashed in pendingBlobs
        // when this op was queued (it can't survive in the queue payload
        // itself). Upload it now so we don't write an empty thumbnailUrl.
        if (!thumbnailUrl) {
          const blob = await getPendingBlob(photo.id);
          if (blob) {
            const storageRef = ref(storage, `users/${userId}/thumbnails/${photo.id}.jpg`);
            await uploadBytes(storageRef, blob);
            thumbnailUrl = await getDownloadURL(storageRef);
            await deletePendingBlob(photo.id);
          }
        }

        const firestorePhoto = { ...photo, thumbnailUrl, thumbnailDataUrl: null };
        await setDoc(doc(firestore, 'users', userId, 'photos', photo.id), firestorePhoto);
        flushed++;
      } else if (op.op === 'delete') {
        const { photoId } = op.payload;
        await deleteDoc(doc(firestore, 'users', userId, 'photos', photoId));
        const storageRef = ref(storage, `users/${userId}/thumbnails/${photoId}.jpg`);
        await deleteObject(storageRef).catch(() => {}); // ignore not-found
        await deletePendingBlob(photoId);
        flushed++;
      } else if (op.op === 'setHunt') {
        const { hunt } = op.payload;
        await setDoc(doc(firestore, 'users', userId, 'hunts', hunt.id), hunt);
        flushed++;
      } else if (op.op === 'deleteHunt') {
        const { huntId } = op.payload;
        await deleteDoc(doc(firestore, 'users', userId, 'hunts', huntId));
        flushed++;
      }

      await clearQueuedOp(op.id);
    } catch (err) {
      console.error('Sync op failed:', op, err);
      errors.push({ op, err });
      // Don't clear — will retry next flush
    }
  }

  return { flushed, errors };
}
